import { motion } from "framer-motion";
import { ArrowUpRight, MessagesSquare, FileSearch, Video, Network } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const CAREER_IMG = "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwbWVudG9yJTIwY2FyZWVyJTIwaW50ZXJ2aWV3JTIwYWklMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4OTIxOTI0NXww&ixlib=rb-4.1.0&q=85";

const icons = [MessagesSquare, FileSearch, Video, Network];

export const CareerReadiness = ({ onOpenChat }) => {
  const { t } = useLanguage();
  return (
    <section id="careers" className="py-20 sm:py-28 bg-[#121516] text-[#FDFCF7]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}
            className="text-xs font-mono uppercase tracking-[0.22em] text-amber-400 font-semibold">
            {t.careers.overline}
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mt-4" data-testid="careers-heading">
            {t.careers.heading}
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 text-sm sm:text-base leading-relaxed text-[#FDFCF7]/60 max-w-lg">
            {t.careers.desc}
          </motion.p>
          <div className="mt-10 space-y-0">
            {t.careers.features.map((f, i) => {
              const Icon = icons[i];
              return (
                <motion.div key={f.title}
                  initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                  className="flex gap-5 py-5 border-t border-[#FDFCF7]/10 last:border-b group" data-testid={`career-feature-${i}`}>
                  <span className="shrink-0 w-11 h-11 rounded-full border border-[#FDFCF7]/15 flex items-center justify-center group-hover:border-[#C85A17] group-hover:bg-[#C85A17]/10 transition-colors duration-300">
                    <Icon className="w-5 h-5 text-amber-400" />
                  </span>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">{f.title}</h3>
                    <p className="text-sm text-[#FDFCF7]/55 mt-1 leading-relaxed">{f.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <motion.button initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.35 }}
            data-testid="career-readiness-scanner-tab" onClick={onOpenChat}
            className="group mt-9 inline-flex items-center gap-2 bg-[#C85A17] text-[#FDFCF7] font-semibold rounded-full px-6 py-3.5 hover:bg-[#a84a12] transition-colors duration-200">
            {t.careers.cta}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </motion.button>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative">
          <div className="overflow-hidden rounded-2xl border border-[#FDFCF7]/10">
            <img src={CAREER_IMG} alt="Mentor coaching a learner in a mock interview" className="w-full aspect-[4/5] sm:aspect-[3/3.4] object-cover" loading="lazy" />
          </div>
          <div className="absolute -bottom-4 left-6 bg-[#163832] border border-[#FDFCF7]/10 rounded-full px-5 py-2.5 text-xs font-mono tracking-wider text-[#FDFCF7]/85 shadow-xl">
            {t.careers.imageCaption}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
